/**
 * Application Routes
 *
 * This file serves as a wrapper around both backend (Rails) and frontend routes.
 * It provides a consistent interface for accessing all routes in the application.
 *
 * Example usage:
 * - Routes.backend.authPath('google')  // Backend route to Google OAuth
 * - Routes.frontend.dashboardPath()    // Frontend route to the dashboard
 */

import * as BackendRoutes from './api/routes';

// Backend routes are generated by ts_routes-rails
const backend = {
  ...BackendRoutes,
  apiV1AdminUserCreditTransactionsPath: (userId: number | string) =>
    `/api/v1/admin/users/${userId}/credit_transactions`,
  authPath: (provider: string) => `/auth/${provider}`,
  profileExportPdfPath: (id: number, sections?: string[]) => {
    const base = `/api/v1/profiles/${id}/export_pdf`;
    if (!sections?.length) return base;
    const params = new URLSearchParams();
    sections.forEach((s) => params.append('sections[]', s));
    return `${base}?${params.toString()}`;
  },
  organizationExportPdfPath: (id: number, sections?: string[]) => {
    const base = `/api/v1/organizations/${id}/export_pdf`;
    if (!sections?.length) return base;
    const params = new URLSearchParams();
    sections.forEach((s) => params.append('sections[]', s));
    return `${base}?${params.toString()}`;
  },
  relationshipExportDetailsPdfPath: (profileA: number, profileB: number, linkStrength?: number) => {
    const params = new URLSearchParams({
      profile_a: profileA.toString(),
      profile_b: profileB.toString(),
    });
    if (linkStrength !== undefined) {
      params.append('link_strength', linkStrength.toString());
    }
    return `/api/v1/relationship_mapping/export_details_pdf?${params}`;
  },
  apiV1AdminUserResetPasswordPath: (id: number) => `/api/v1/admin/users/${id}/reset_password`,
};

// Frontend routes are defined manually here to match the SPA routing
const frontend = {
  rootPath: '/',
  subscriptionsPath: '/subscriptions',
  trackedSubjectEventsPath: (trackedSubjectId: number) => `/subscriptions/tracked-subjects/${trackedSubjectId}/events`,
  searchPath: '/',
  clientHomePath: '/client-home',
  profileRequestsPath: '/profile-requests',
  profilePath: (id: number) => `/profiles/${id}`,
  organizationPath: (id: number) => `/organizations/${id}`,
  loginPath: '/login',
  registrationPath: '/registration',
  resetPasswordPath: '/reset-password',
  confirmResetPasswordPath: '/confirm-reset-password',
  adminPath: '/admin',
  adminAccountsPath: '/admin/accounts',
  adminAccountPath: (id: number) => `/admin/accounts/${id}`,
  adminUsersPath: '/admin/users',
  adminUserPath: (id: number) => `/admin/users/${id}`,
  adminProfilesPath: '/admin/profiles',
  adminProfilePath: (id: number) => `/admin/profiles/${id}`,
  adminOrganizationsPath: '/admin/organizations',
  adminOrganizationPath: (id: number) => `/admin/organizations/${id}`,
  adminOrganizationDraftEditPath: (id: number) => `/admin/organization/${id}/draft_version`,
  adminInterestsPath: '/admin/interests',
  adminIndustriesPath: '/admin/industries',
  adminBusinessFunctionsPath: '/admin/business-functions',
  adminQuartrEventTranscriptsPath: '/admin/quartr-event-transcripts',
  adminQuartrEventTranscriptPath: (id: number) => `/admin/quartr-event-transcripts/${id}`,
  adminRequestsPath: '/admin/requests',
  adminRequestsBoardPath: '/admin/requests/board',
  adminRequestPath: (id: number) => `/admin/requests/${id}`,
  adminDashboardsPath: '/admin/dashboards',
  adminValueDashboardPath: '/admin/value-dashboard',
  adminEvaluations: '/admin/evaluations',
  adminEvaluationPath: (id: number) => `/admin/evaluations/${id}`,
  adminEvaluationMetricPath: (id: number) => `/admin/evaluation-metrics/${id}`,
  relationshipsPath: '/relationships',
  userProfilePath: '/profile',
} as const;

// Combine both types of routes into a single export
export const Routes = {
  backend,
  frontend,
};

export default Routes;
